import { ClockCounterClockwise } from '@phosphor-icons/react';
import React from 'react';

import { Pagination, Select } from 'antd';
import { formatDate } from '../../../../../../../utils/DateUtils';
import { IOdometerHistory } from '../../OdometerHistory';
import { formatNumberCommas } from '../../../../../../../utils/CurrencyUtils';

interface OdometerHistoryTableProps {
  odometerHistory: IOdometerHistory[];
  odometerHistoryCount: number;
  offset: number;
  limit: number;
  perPage: number;
  handlePerPageChange: (value: number) => void;
  handlePageChange: (page: number, pageSize: number) => void;
}

const TABLE_HEADERS = [
  'Requested On',
  'Submitted On',
  'Odometer',
  'Submitted By',
];

export const OdometerHistoryTable: React.FC<OdometerHistoryTableProps> = ({
  odometerHistory,
  offset,
  limit,
  odometerHistoryCount,
  perPage,
  handlePerPageChange,
  handlePageChange,
}) => {
  const renderPagination = () => (
    <div
      className={`flex w-full items-center justify-between gap-4 rounded-b-[8px] border-[1px] border-bordergray bg-white px-4 py-3 sm:flex-col md:flex-row`}
    >
      <div className="flex flex-row items-center space-x-2 sm:min-h-[48px] sm:w-full sm:justify-between md:w-[50%]">
        {odometerHistoryCount > 0 && (
          <p>
            <span className="text-graytertiary">Showing</span> {offset + 1} -{' '}
            {Math.min(offset + limit, odometerHistoryCount)}{' '}
            <span className="text-graytertiary">of</span>{' '}
            {odometerHistoryCount}{' '}
          </p>
        )}
        <Select
          value={perPage}
          onChange={handlePerPageChange}
          className={`w-[40%]`}
        >
          <Select.Option value={20}>20 per page</Select.Option>
          <Select.Option value={50}>50 per page</Select.Option>
          <Select.Option value={100}>100 per page</Select.Option>
        </Select>
      </div>

      <Pagination
        current={Math.floor(offset / limit) + 1}
        pageSize={perPage}
        total={odometerHistoryCount}
        onChange={handlePageChange}
        showSizeChanger={false}
      />
    </div>
  );

  return (
    <div className="flex h-full w-full flex-col justify-between overflow-hidden rounded-[8px] border-l-[1px] border-r-[1px] border-t-[1px] border-bordergray bg-white">
      {odometerHistory && odometerHistory?.length > 0 ? (
        <>
          <div className="w-full overflow-x-auto">
            <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
              <thead className="bg-gray-100">
                <tr>
                  {TABLE_HEADERS.map((header) => (
                    <th
                      key={header}
                      className="px-4 py-2 text-left text-[14px] font-[600] text-primary"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="overflow-y-auto">
                {odometerHistory.map((asset, index) => {
                  return (
                    <tr
                      key={index}
                      className="border-b border-gray-200 hover:cursor-pointer hover:bg-ctaactivegreen"
                    >
                      <td className="px-4 py-2 text-[14px] text-primary">
                        {formatDate(
                          new Date(asset.created_at ?? ''),
                          true,
                          true
                        )}
                      </td>
                      <td className="px-4 py-2 text-[14px] text-primary">
                        {formatDate(
                          new Date(asset.manually_completed_at ?? ''),
                          true,
                          true
                        )}
                      </td>
                      <td className="px-4 py-2 text-[14px] text-primary">
                        {formatNumberCommas(asset.cumulative_odometer)}
                      </td>
                      <td className="px-4 py-2 text-[14px] text-primary">
                        {asset?.driver?.first_name && asset?.driver?.last_name
                          ? `${asset.driver.first_name} ${asset.driver.last_name}`
                          : '--'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
          <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
            <ClockCounterClockwise size={40} className="text-graysecondary" />
          </div>
          <p className="text-[14px] leading-[20px] text-graysecondary">
            No Odometer history found.
          </p>
        </div>
      )}
    </div>
  );
};
